<template>
  <v-row :gap="[32, 24]">
    <v-col cols="12">
      <v-text-field
        v-model.trim="person.address1"
        :error-messages="missingMessage('address1')"
        :rules="[maxLength(150)]"
        maxlength="150"
      >
        <template #label>
          {{ t("treezorOnboarding.address1") }}
          <span class="text-error">*</span>
        </template>
      </v-text-field>
    </v-col>
    <v-col cols="12" md="6">
      <v-text-field
        v-model.trim="person.address2"
        :rules="[maxLength(150)]"
        maxlength="150"
        :label="t('treezorOnboarding.address2')"
      />
    </v-col>
    <v-col cols="12" md="6">
      <v-text-field
        v-model.trim="person.address3"
        :rules="[maxLength(150)]"
        maxlength="150"
        :label="t('treezorOnboarding.address3')"
      />
    </v-col>
    <v-col cols="12" md="6">
      <v-text-field v-model.trim="person.postcode" :error-messages="missingMessage('postcode')">
        <template #label>
          {{ t("treezorOnboarding.postcode") }}
          <span class="text-error">*</span>
        </template>
      </v-text-field>
    </v-col>
    <v-col cols="12" md="6">
      <v-text-field v-model.trim="person.city" :error-messages="missingMessage('city')">
        <template #label>
          {{ t("treezorOnboarding.city") }}
          <span class="text-error">*</span>
        </template>
      </v-text-field>
    </v-col>
    <v-col cols="12" md="6">
      <v-autocomplete
        v-model="person.country"
        :error-messages="missingMessage('country')"
        :items="isoCountryItems"
        clearable
      >
        <template #label>
          {{ t("treezorOnboarding.country") }}
          <span class="text-error">*</span>
        </template>
      </v-autocomplete>
    </v-col>
    <v-col cols="12" md="6">
      <v-text-field
        v-model.trim="person.phone"
        :error-messages="missingMessage('phone')"
        :rules="[validPhoneNumber]"
        :hint="t('treezorOnboarding.phoneHint')"
        persistent-hint
      >
        <template #label>
          {{ t("common.phone") }}
          <span class="text-error">*</span>
        </template>
      </v-text-field>
    </v-col>
  </v-row>
</template>

<script lang="ts" setup>
import { useI18n } from "@/composables/useI18n";
import { useRules } from "@/composables/useRules";
import type { TreezorKycEffectiveBeneficiaryField } from "@aircap/common/treezor";
import { useTreezorOnboarding } from "./useTreezorOnboarding";

const props = defineProps<{ missingFields: readonly TreezorKycEffectiveBeneficiaryField[] }>();
const person = defineModel<{
  phone: string;
  address1: string;
  address2: string;
  address3: string;
  postcode: string;
  city: string;
  country: string | null;
}>({ required: true });

const { t } = useI18n();
const { validPhoneNumber, maxLength, required } = useRules();
const { isoCountryItems } = useTreezorOnboarding();

const missingMessage = (field: "phone" | "address1" | "postcode" | "city" | "country") =>
  props.missingFields.includes(field) && required(person.value[field]) !== true
    ? t("errors.required")
    : [];
</script>
